import React, { useState } from 'react';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import ModalDialog from '../../../components/organisms/ModalDialog/ModalDialog';
import { FileUpload, Form, Input, Row, SubmitButton, Switch, Textarea, useForm } from '../../../_lib/Form2';
import { useDelayed } from '../../../hooks/useDelayed';
import { SharedFile } from '../SharedFiles.types';
import {
  CategoriesComboboxMultiSelect,
  TermSuggestComboBoxMultiSelect,
} from '../../../components/molecules/Form/components/ComboBox';
import { ModalDialogModifier } from '../../../components/organisms/ModalDialog/ModalDialog.modifier';
import { HiddenInput } from '../../../_lib/Form2/elements/HiddenInput/HiddenInput';

type SharedFilesItemEditForm = {
  closeModal: () => void;
  file: SharedFile;
};

/**
 * Form to edit a single shared file.
 */
export function SharedFilesItemEditForm({ closeModal, file }: SharedFilesItemEditForm) {
  const { t } = useSonTranslation('SharedFiles');
  const delayedModalClose = useDelayed(closeModal);

  const { formProps, params, fetchState } = useForm({
    form: file!.forms!.edit!,
    onSuccess: () => delayedModalClose(),
  });
  const [isImportantChange, setIsImportantChange] = useState<boolean>();

  const hiddenFileReferenceId = params.getByName('fileReferenceId');
  const titleParam = params.getByName('title');
  const check = params.getByName('importantChange');
  const changeCommentParam = params.getByName('changeComment');
  const fileData = params.getByName('fileData');
  const description = params.getByName('description');
  const author = params.getByName('author');
  const contentDate = params.getByName('contentDate');
  const keywords = params.getByName('keywords');
  // optional
  const categories = params.findByName('categories');

  return (
    <ModalDialog
      closeModal={closeModal}
      headline={t('forms.edit.modalHeadline', { name: file.fileTitle || file.fileName })}
      initialFocusIndex={1}
      modifier={ModalDialogModifier.HIGH}
    >
      <Form {...formProps}>
        <HiddenInput {...hiddenFileReferenceId} />
        <Row>
          <Input label={t('forms.edit.title')} param={titleParam} />
        </Row>
        <Row>
          <Textarea label={t('forms.edit.description')} param={description} />
        </Row>
        <FileUpload headingLabel={t('forms.edit.file')} param={fileData} formRef={formProps.ref} />
        <Row>
          <Input label={t('forms.edit.author')} param={author} />
        </Row>
        <Row>
          <Input label={t('forms.edit.contentDate')} param={contentDate} />
        </Row>
        <Row>
          <TermSuggestComboBoxMultiSelect
            name={keywords.name}
            suggestUrl={keywords.comboBoxUrl.url}
            maxLength={keywords.constraints?.maxLength}
            maxElements={keywords.constraints?.maxElements}
            value={keywords.value}
            label={t('forms.edit.terms')}
          />
        </Row>
        {categories && (
          <Row>
            <CategoriesComboboxMultiSelect
              name={categories.name}
              value={categories.value}
              options={categories.options}
            />
          </Row>
        )}
        <Row>
          <Switch
            label={t('forms.edit.importantChange')}
            param={check}
            onChange={event => setIsImportantChange(event.target.checked)}
          />
        </Row>
        {isImportantChange && (
          <Row>
            <Textarea label={t('forms.edit.changeComment')} param={changeCommentParam} />
          </Row>
        )}
        <SubmitButton
          buttonLabel={t('forms.edit.submitButton')}
          messageSuccess={t('forms.edit.success')}
          messageSending={t('forms.edit.sending')}
          messageFail={t('forms.edit.fail')}
          fetchState={fetchState}
          active={fetchState.type !== 'loading' && fetchState.type !== 'success'}
        />
      </Form>
    </ModalDialog>
  );
}
